<script setup lang="ts">
import { useInfiniteScroll } from '@vueuse/core'
import type { HTMLAttributes } from 'vue'
import { ChevronDown, X } from 'lucide-vue-next'
import { cn } from '@/ui/lib/utils'

export type Option = { value: string | number; label: string; disabled?: boolean }
export type OptionGroup = { label: string; items: Option[] }

export type MultiSelectProps = {
  options: Option[] | OptionGroup[]
  disabled?: boolean
  display?: string
  maxSelectedLabels?: number
  filter?: boolean
  filterFields?: string[]
  filterMatchMode?: string
  filterLocale?: string
  filterIcon?: string
  filterPlaceholder?: string
  fluid?: boolean
  placeholder?: string
  emptyFilterMessage?: string
  emptyMessage?: string
  selectedItemsLabel?: string
  showToggleAll?: boolean
  clearable?: boolean
  ariaLabel?: string
  ariaLabelledby?: string
  grouped?: boolean
  allSelectedText?: string | null
  hasMore?: boolean
  isLoadingMore?: boolean
  isFiltering?: boolean
  loading?: boolean
  class?: HTMLAttributes['class']
  pt?: Record<string, unknown>
}

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(defineProps<MultiSelectProps>(), {
  disabled: false,
  display: 'chip',
  maxSelectedLabels: 4,
  filter: true,
  fluid: true,
  grouped: false,
  allSelectedText: null,
  hasMore: false,
  isLoadingMore: false,
  isFiltering: false,
  showToggleAll: true,
  loading: false
})

const { t } = useI18n()

const wrapperClass = computed(() => props.class)

const modelValue = defineModel<string[] | number[] | null>('modelValue', { default: [] })

// ── Infinite scroll (preserve existing hasMore/loadMore API) ──────────────────
const panelElement = ref<HTMLElement | null>(null)
const commandListRef = ref<{ $el?: HTMLElement } | null>(null)

useInfiniteScroll(
  panelElement,
  () => {
    if (props.hasMore && !props.isLoadingMore) {
      emit('loadMore')
    }
  },
  {
    distance: 10,
    canLoadMore: () => props.hasMore && !props.isLoadingMore
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[] | number[] | null): void
  (e: 'filter', value: string): void
  (e: 'loadMore'): void
  (e: 'show'): void
}>()

// ── Popover open state ────────────────────────────────────────────────────────
const open = ref(false)

const onOpenChange = (val: boolean) => {
  open.value = val
  if (val) {
    if (props.hasMore) {
      nextTick(() => {
        panelElement.value = commandListRef.value?.$el ?? null
      })
    }
    emit('show')
  }
}

// ── Grouped options support ──────────────────────────────────────────────────
const groups = computed<OptionGroup[]>(() => (props.grouped ? (props.options as OptionGroup[]) : []))

const allOptions = computed<Option[]>(() =>
  props.grouped ? groups.value.flatMap((g) => g.items) : (props.options as Option[])
)

// ── Derived value helpers ────────────────────────────────────────────────────
const selectedValues = computed<Array<string | number>>(() =>
  Array.isArray(modelValue.value) ? (modelValue.value as Array<string | number>) : []
)

const selectedOptions = computed(() => allOptions.value.filter((opt) => selectedValues.value.includes(opt.value)))

// ── "All selected" logic ─────────────────────────────────────────────────────
const isAllSelectedAndHasCustomText = computed(() => {
  return props.allSelectedText && selectedValues.value.length === allOptions.value.length
})

// ── Display label ─────────────────────────────────────────────────────────────
const triggerLabel = computed<string | null>(() => {
  if (isAllSelectedAndHasCustomText.value) {
    return props.allSelectedText as string
  }
  if (selectedValues.value.length === 0) {
    return null
  }
  if (selectedValues.value.length <= props.maxSelectedLabels) {
    // chips will render; no summary label needed
    return null
  }
  // More than maxSelectedLabels → show summary
  return (
    props.selectedItemsLabel ??
    t('global.multiselect.default.selectedItemsLabel', { count: selectedValues.value.length })
  )
})

// ── Locked options ────────────────────────────────────────────────────────────
// A disabled option keeps its current state: no global action may select or drop it.
const lockedSelectedValues = computed<Array<string | number>>(() =>
  allOptions.value.filter((opt) => opt.disabled && selectedValues.value.includes(opt.value)).map((opt) => opt.value)
)

const isRemovable = (opt: Option): boolean => !props.disabled && !opt.disabled

// ── "Select all" computed ─────────────────────────────────────────────────────
const isAllSelected = computed<boolean>(() => {
  const selectableOptions = allOptions.value.filter((o) => !o.disabled)
  if (selectableOptions.length === 0) return false
  return selectableOptions.every((opt) => selectedValues.value.includes(opt.value))
})

// ── Toggle helpers ────────────────────────────────────────────────────────────
const isSelected = (opt: Option): boolean => selectedValues.value.includes(opt.value)

const toggle = (opt: Option) => {
  if (props.disabled || opt.disabled) return
  const next = isSelected(opt)
    ? selectedValues.value.filter((v) => v !== opt.value)
    : [...selectedValues.value, opt.value]
  modelValue.value = next as typeof modelValue.value
}

const removeChip = (value: string | number) => {
  const opt = allOptions.value.find((o) => o.value === value)
  if (props.disabled || opt?.disabled) return
  modelValue.value = selectedValues.value.filter((v) => v !== value) as typeof modelValue.value
}

const showClearButton = computed(
  () => props.clearable && !props.disabled && selectedValues.value.length > lockedSelectedValues.value.length
)

const clearAll = () => {
  if (props.disabled) return
  modelValue.value = [...lockedSelectedValues.value] as typeof modelValue.value
}

const onSelectAllChange = (checked: boolean) => {
  if (props.disabled) return
  const selectableValues = allOptions.value.filter((o) => !o.disabled).map((o) => o.value)
  modelValue.value = (
    checked ? [...lockedSelectedValues.value, ...selectableValues] : [...lockedSelectedValues.value]
  ) as typeof modelValue.value
}

// ── Filter ────────────────────────────────────────────────────────────────────
const filterSearch = ref('')

const onFilterInput = (val: string) => {
  filterSearch.value = val
  emit('filter', val)
}

// ── Whether to show chips inline ──────────────────────────────────────────────
const showChipsInline = computed(() => {
  if (isAllSelectedAndHasCustomText.value) return false
  if (triggerLabel.value !== null) return false // summary label mode
  return selectedOptions.value.length > 0
})
</script>

<template>
  <Popover :open="open" @update:open="onOpenChange">
    <PopoverTrigger as-child :disabled="disabled">
      <button
        type="button"
        :class="
          cn(
            'flex h-10 w-full cursor-pointer items-center justify-between rounded-control border border-input bg-background px-3 py-1 text-sm shadow-xs',
            disabled && 'cursor-not-allowed bg-muted text-foreground-disabled',
            !fluid && 'w-auto',
            wrapperClass
          )
        "
        :aria-label="ariaLabel ?? undefined"
        :aria-labelledby="ariaLabelledby ?? undefined"
      >
        <div class="flex min-w-0 flex-1 flex-nowrap items-center gap-1 overflow-hidden">
          <!-- All-selected custom text -->
          <template v-if="isAllSelectedAndHasCustomText">
            <span class="text-sm text-foreground">{{ allSelectedText }}</span>
          </template>

          <!-- Summary label (more than maxSelectedLabels) -->
          <template v-else-if="triggerLabel !== null">
            <span class="text-sm text-foreground">{{ triggerLabel }}</span>
          </template>

          <!-- Chip display -->
          <template v-else-if="showChipsInline">
            <template v-for="opt in selectedOptions" :key="opt.value">
              <slot name="chip" :value="opt.value" :option="opt" :remove="() => removeChip(opt.value)">
                <BaseChip
                  :text="opt.label"
                  :removable="isRemovable(opt)"
                  class="shrink-0"
                  @remove="removeChip(opt.value)"
                />
              </slot>
            </template>
          </template>

          <!-- Empty placeholder -->
          <span v-else class="text-sm text-muted-foreground">
            {{ placeholder ?? t('global.multiselect.default.placeholder') }}
          </span>
        </div>

        <span v-if="loading" class="ml-2 inline-flex h-4 w-4 shrink-0 items-center justify-center">
          <div class="h-4 w-4 animate-spin rounded-full border-2 border-border border-l-transparent" />
        </span>
        <span v-else class="ml-2 flex shrink-0 items-center gap-1">
          <span
            v-if="showClearButton"
            role="button"
            tabindex="0"
            :aria-label="t('global.words.clear')"
            class="rounded-sm p-0.5 text-red-500 hover:bg-red-50 hover:text-red-600"
            @click.stop="clearAll"
            @keydown.enter.stop.prevent="clearAll"
            @keydown.space.stop.prevent="clearAll"
          >
            <X class="size-3.5" />
          </span>
          <ChevronDown class="size-4 text-muted-foreground" />
        </span>
      </button>
    </PopoverTrigger>

    <PopoverContent class="w-[var(--reka-popover-trigger-width)] p-0" align="start" :side-offset="4">
      <Command :highlight-on-hover="true">
        <!-- Filter input -->
        <CommandInput
          v-if="filter"
          :placeholder="filterPlaceholder ?? t('global.multiselect.default.filterPlaceholder')"
          @update:model-value="onFilterInput"
        />

        <!-- Select-all row -->
        <div v-if="showToggleAll && allOptions.length > 1" class="border-b p-1">
          <button
            type="button"
            class="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
            @click="onSelectAllChange(!isAllSelected)"
          >
            <Checkbox :model-value="isAllSelected" tabindex="-1" aria-hidden="true" class="pointer-events-none" />
            {{
              isAllSelected
                ? t('global.multiselect.default.deselectAll', 'Tout désélectionner')
                : t('global.multiselect.default.selectAll', 'Tout sélectionner')
            }}
          </button>
        </div>

        <CommandList ref="commandListRef">
          <!-- Custom header slot -->
          <div v-if="$slots.header" class="mt-2">
            <slot name="header" />
          </div>

          <CommandEmpty>
            <slot name="empty">
              <div class="p-3 text-center text-muted-foreground">
                {{
                  filterSearch
                    ? (emptyFilterMessage ?? t('global.multiselect.default.searchingMessage'))
                    : (emptyMessage ?? t('global.multiselect.default.searchingMessage'))
                }}
              </div>
            </slot>
          </CommandEmpty>

          <!-- Grouped mode -->
          <template v-if="grouped">
            <CommandGroup v-for="group in groups" :key="group.label">
              <slot name="optiongroup" :option="group">
                <div class="mt-2 px-2 text-xs font-normal uppercase text-muted-foreground">{{ group.label }}</div>
              </slot>
              <CommandItem
                v-for="option in group.items"
                :key="String(option.value)"
                :value="option.label"
                :disabled="option.disabled"
                @select="toggle(option)"
              >
                <Checkbox
                  :model-value="isSelected(option)"
                  tabindex="-1"
                  aria-hidden="true"
                  class="pointer-events-none mr-2"
                />
                <slot name="option" :option="option">
                  {{ option.label }}
                </slot>
              </CommandItem>
            </CommandGroup>
          </template>

          <!-- Flat mode -->
          <CommandGroup v-else>
            <CommandItem
              v-for="option in options as Option[]"
              :key="String(option.value)"
              :value="option.label"
              :disabled="option.disabled"
              @select="toggle(option)"
            >
              <Checkbox
                :model-value="isSelected(option)"
                tabindex="-1"
                aria-hidden="true"
                class="pointer-events-none mr-2"
              />
              <slot name="option" :option="option">
                {{ option.label }}
              </slot>
            </CommandItem>
          </CommandGroup>

          <!-- Custom footer slot -->
          <div v-if="$slots.footer">
            <slot name="footer" />
          </div>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
